import React, { useRef } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../../lib/api";
import { GAME_PHASES, phaseTitle } from "../../core/phases.js";
import {
  FD_CLOSED_HINT,
  FD_CLOSED_TITLE,
  FD_EMPTY_ATTENDEES,
  FD_EMPTY_ATTENDEES_HINT,
  FD_LOAD_ERROR_TITLE,
  FD_RESULTS_DONE,
  FD_VOTE_CTA,
  FD_VOTE_TITLE,
  FD_WELCOME_TITLE,
  fdVoteSignalHint,
} from "../copy.js";
import { useFavoriteDancesSession } from "../hooks/useFavoriteDancesSession.js";
import { ReadableNavButton } from "../../../components/Sticker";
import { FieldHint } from "../../../components/createEvent/CreateEventFormUi";
import {
  WorkflowModalBody,
  WorkflowModalFooter,
  WorkflowModalHeader,
  WorkflowModalShell,
} from "../../../components/workflow/WorkflowModal";
import DeclineStep from "../steps/DeclineStep.jsx";
import PlayStep from "../steps/PlayStep.jsx";
import ResultsStep from "../steps/ResultsStep.jsx";
import WelcomeStep from "../steps/WelcomeStep.jsx";

const TITLE_ID = "favorite-dances-sheet-title";

function SheetEmptyState({ closed }) {
  if (closed) {
    return (
      <div className="py-8 text-center" data-testid="favorite-dances-closed">
        <p className="font-ui font-semibold text-[16px] text-ink">{FD_CLOSED_TITLE}</p>
        <FieldHint className="mt-2 text-ink/70">{FD_CLOSED_HINT}</FieldHint>
      </div>
    );
  }

  return (
    <div className="py-8 text-center" data-testid="favorite-dances-empty">
      <p className="font-ui font-semibold text-[16px] text-ink">{FD_EMPTY_ATTENDEES}</p>
      <FieldHint className="mt-2 text-ink/70">{FD_EMPTY_ATTENDEES_HINT}</FieldHint>
    </div>
  );
}

function SheetLoadError({ message, onRetry }) {
  return (
    <div className="py-8 text-center" data-testid="favorite-dances-load-error" role="alert">
      <p className="font-ui font-semibold text-[16px] text-ink">{FD_LOAD_ERROR_TITLE}</p>
      <FieldHint className="mt-2 text-ink/75 break-words">{message}</FieldHint>
      <ReadableNavButton
        type="button"
        variant="default"
        className="mt-4 min-h-[44px] px-5 text-[14px]"
        onClick={onRetry}
        data-testid="favorite-dances-retry"
      >
        Try again
      </ReadableNavButton>
    </div>
  );
}

export default function FavoriteDancesSheet({
  open,
  onClose,
  eventId,
  occurrenceDate,
  dancerId,
  onSubmitted,
}) {
  const dialogRef = useRef(null);
  const session = useFavoriteDancesSession({
    enabled: open,
    eventId,
    occurrenceDate,
    dancerId,
    onError: (message) => toast.error(message),
    onSubmitted,
  });

  const {
    loading,
    loadError,
    state,
    phase,
    selected,
    busy,
    reload,
    optIn,
    decline,
    submit,
    dismiss,
    togglePick,
  } = session;

  if (!open) return null;

  const maxPicks = state?.max_picks || 3;
  const attendees = state?.eligible_attendees || [];
  const windowOpen = state?.vote_window_open !== false;
  const sheetTitle = phaseTitle(phase, {
    welcomeTitle: FD_WELCOME_TITLE,
    playTitle: FD_VOTE_TITLE,
  });

  async function handleDismiss() {
    await dismiss();
    onClose();
  }

  async function handleSubmit() {
    if (busy || selected.length === 0 || !windowOpen) return;
    try {
      await submit();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const showVoteFooter =
    phase === GAME_PHASES.PLAY && !loading && !loadError && windowOpen && attendees.length > 0;
  const showResultsFooter = phase === GAME_PHASES.RESULTS && !loading && !loadError;
  const canSubmit = selected.length > 0 && !busy && windowOpen;

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={handleDismiss}
      onBackdropClose={busy ? undefined : handleDismiss}
      testId="favorite-dances-sheet"
      titleId={TITLE_ID}
      busy={loading || busy}
      layout="sheet"
      maxWidthClass="w-full sm:max-w-page"
    >
      <WorkflowModalHeader
        title={sheetTitle}
        titleId={TITLE_ID}
        onClose={busy ? undefined : handleDismiss}
        closeTestId="favorite-dances-close"
        sticky
      />

      <WorkflowModalBody sheet className="space-y-4">
        {loading ? (
          <div
            className="flex flex-col items-center justify-center gap-3 py-12"
            aria-busy="true"
            aria-label="Loading dancers"
          >
            <Loader2 className="w-6 h-6 text-plum animate-spin motion-reduce:animate-none" strokeWidth={2.5} />
            <p className="font-ui font-medium text-[14px] text-ink/70">Loading dancers…</p>
          </div>
        ) : loadError ? (
          <SheetLoadError message={loadError} onRetry={reload} />
        ) : !windowOpen && phase !== GAME_PHASES.DECLINED ? (
          <SheetEmptyState closed />
        ) : phase === GAME_PHASES.WELCOME ? (
          attendees.length === 0 ? (
            <SheetEmptyState closed={false} />
          ) : (
            <WelcomeStep
              attendees={attendees}
              eventTotalPicks={state?.event_total_picks ?? 0}
              onOptIn={optIn}
              onDecline={decline}
              busy={busy}
            />
          )
        ) : phase === GAME_PHASES.PLAY ? (
          attendees.length === 0 ? (
            <SheetEmptyState closed={false} />
          ) : (
            <PlayStep
              attendees={attendees}
              selected={selected}
              maxPicks={maxPicks}
              onToggle={togglePick}
              onSubmit={handleSubmit}
              submitting={busy}
              footer={false}
              signalMinAppreciations={state?.event_signal_min_appreciations}
            />
          )
        ) : phase === GAME_PHASES.RESULTS ? (
          <ResultsStep
            myAppreciationsReceived={state?.my_appreciations_received ?? 0}
            myPickCount={state?.my_votee_ids?.length ?? 0}
            eventTotalPicks={state?.event_total_picks ?? 0}
            eventSignalMinAppreciations={state?.event_signal_min_appreciations}
            crewDistinctCount={state?.crew_distinct_count ?? 0}
            crewNudgeMinDistinct={state?.crew_nudge_min_distinct}
            onDone={onClose}
            footer={false}
          />
        ) : phase === GAME_PHASES.DECLINED ? (
          <DeclineStep onClose={onClose} />
        ) : null}
      </WorkflowModalBody>

      {showResultsFooter && (
        <WorkflowModalFooter sticky safeArea>
          <ReadableNavButton
            type="button"
            variant="night"
            className="w-full min-h-[44px] py-3.5 text-[15px]"
            onClick={onClose}
            data-testid="favorite-dances-results-done"
          >
            {FD_RESULTS_DONE}
          </ReadableNavButton>
        </WorkflowModalFooter>
      )}

      {showVoteFooter && (
        <WorkflowModalFooter sticky safeArea className="space-y-2">
          <ReadableNavButton
            type="button"
            variant="primary"
            className="w-full py-3.5 text-[15px]"
            disabled={!canSubmit}
            onClick={handleSubmit}
            data-testid="favorite-dances-submit"
          >
            {busy ? "Sending…" : FD_VOTE_CTA}
          </ReadableNavButton>
          <p className="font-ui text-[12px] text-center text-ink/65 text-pretty" data-testid="favorite-dances-footer">
            {fdVoteSignalHint(state?.event_signal_min_appreciations)}
          </p>
        </WorkflowModalFooter>
      )}
    </WorkflowModalShell>
  );
}
